import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { unifiedClient } from '@/lib/unified-fetch';
import { message } from 'antd';
import type { components } from '@/@types/openapi-admin';

// Type definitions
export type CurrencyRate = components['schemas']['def-20'];
export type CurrencyRateCreate = components['schemas']['def-21'];
export type CurrencyRateUpdate = components['schemas']['def-22'];
export type CurrencyRateFilter = components['schemas']['def-23'];

// Mass set request type
export type MassSetRequest = {
  startDate: string;
  endDate: string;
  rate: number;
};

// Fetch currency rates with pagination and filtering
export const useCurrencyRates = (
  limit: number = 50,
  offset: number = 0,
  filter?: CurrencyRateFilter,
  sort?: { field?: string; order?: 'ASC' | 'DESC' }
) => {
  return useQuery({
    queryKey: ['currencyRates', limit, offset, filter, sort],
    queryFn: async () => {
      const response = await unifiedClient.admin.GET('/event_CurrencyRate/', {
        params: {
          query: {
            limit,
            offset,
            filter,
            sort,
          },
        },
      });

      if (!response.data) {
        throw new Error('Failed to fetch currency rates');
      }

      return {
        data: response.data || [],
        total: response.data?.length || 0, // Note: API doesn't seem to return total count
      };
    },
  });
};

// Infinite scroll currency rates hook
export const useInfiniteCurrencyRates = (
  pageSize: number = 100,
  filter?: CurrencyRateFilter,
  sort?: { field?: string; order?: 'ASC' | 'DESC' }
) => {
  return useInfiniteQuery({
    queryKey: ['currencyRates', 'infinite', pageSize, filter, sort],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await unifiedClient.admin.GET('/event_CurrencyRate/', {
        params: {
          query: {
            limit: pageSize,
            offset: pageParam * pageSize,
            filter,
            sort,
          },
        },
      });

      if (!response.data) {
        throw new Error('Failed to fetch currency rates');
      }

      const data = response.data || [];
      return {
        data,
        nextCursor: data.length === pageSize ? pageParam + 1 : undefined,
        hasMore: data.length === pageSize,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
  });
};

// Mass set currency rates for date range
export const useMassSetCurrencyRates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MassSetRequest) => {
      const response = await unifiedClient.admin.POST('/event_CurrencyRate/mass-set', {
        body: data,
      });

      if (!response.data) {
        throw new Error('Failed to set currency rates');
      }

      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate and refetch currency rates
      queryClient.invalidateQueries({ queryKey: ['currencyRates'] });
      message.success(`Successfully set currency rates for ${data?.currencyRates?.length || 0} dates`);
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to set currency rates');
    },
  });
};

// Update multiple currency rates
export const useUpdateCurrencyRates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, data }: { ids: number[]; data: CurrencyRateUpdate }) => {
      const response = await unifiedClient.admin.PATCH('/event_CurrencyRate/{ids}', {
        params: {
          path: { ids },
        },
        body: data,
      });

      if (!response.data) {
        throw new Error('Failed to update currency rates');
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch currency rates
      queryClient.invalidateQueries({ queryKey: ['currencyRates'] });
      message.success(`Successfully updated ${variables.ids.length} currency rate(s)`);
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to update currency rates');
    },
  });
};

// Get specific currency rates by IDs
export const useCurrencyRatesByIds = (ids: number[]) => {
  return useQuery({
    queryKey: ['currencyRates', 'byIds', ids],
    queryFn: async () => {
      const response = await unifiedClient.admin.GET('/event_CurrencyRate/{ids}', {
        params: {
          path: { ids },
        },
      });

      if (!response.data) {
        throw new Error('Failed to fetch currency rates');
      }

      return response.data || [];
    },
    enabled: ids.length > 0,
  });
};

// Delete currency rates
export const useDeleteCurrencyRates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids: number[]) => {
      const response = await unifiedClient.admin.DELETE('/event_CurrencyRate/{ids}', {
        params: {
          path: { ids },
        },
      });

      if (!response.data) {
        throw new Error('Failed to delete currency rates');
      }

      return response.data;
    },
    onSuccess: (data, ids) => {
      // Invalidate and refetch currency rates
      queryClient.invalidateQueries({ queryKey: ['currencyRates'] });
      message.success(`Successfully deleted ${ids.length} currency rate(s)`);
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to delete currency rates');
    },
  });
};
